import Link from "next/link";
import type { PanchangResponse } from "@/lib/api/panchang";
import TithiTypesReference, {
  getTithiTypeInfo,
  type TithiTypeInfo,
} from "./_components/TithiTypesReference";
import { TITHI_CONTROL_DATA } from "./tithi-today.control-data";
import {
  displayPakshaName,
  displayTithiName,
  displayVaraName,
  tithiDateLabel,
  TITHI_COPY,
  type TithiLanguage,
} from "./tithi-today.copy";

type PanchangTithi = PanchangResponse["tithi"];

export function ControlTithiHeader({
  language,
  date,
  tithi,
  cityName,
}: {
  language: TithiLanguage;
  date: string;
  tithi?: PanchangTithi;
  cityName: string;
}) {
  const copy = TITHI_COPY[language];
  const isShukla = tithi?.paksha.toLowerCase().includes("shukla");
  return (
    <div className="mb-8 text-center">
      <div className="mb-3 text-5xl">{isShukla ? "🌕" : "🌑"}</div>
      <h1 className="mb-2 font-serif text-2xl font-bold text-ish-dark sm:text-3xl">
        {copy.headerTitle}
      </h1>
      <p className="text-body-small text-ish-text-muted">
        {copy.headerSubtitle} · {tithiDateLabel(date, language)} · {cityName}
      </p>
    </div>
  );
}

export function ControlMainTithiCard({
  language,
  isLoading,
  isError,
  tithi,
}: {
  language: TithiLanguage;
  isLoading: boolean;
  isError: boolean;
  tithi?: PanchangTithi;
}) {
  const typeInfo = tithi ? getTithiTypeInfo(tithi.number, language) : null;
  return (
    <div className="card-sacred mb-5 min-h-[260px] p-6 text-center">
      {isLoading && <ControlTithiLoading language={language} />}
      {isError && <p className="py-8 text-body-small text-error">{TITHI_COPY[language].error}</p>}
      {!isLoading && !isError && tithi && typeInfo && (
        <ControlTithiDetails language={language} tithi={tithi} typeInfo={typeInfo} />
      )}
    </div>
  );
}

function ControlTithiLoading({ language }: { language: TithiLanguage }) {
  return (
    <div className="py-8">
      <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-ish-accent border-t-transparent" />
      <p className="text-body-small text-ish-text-faint">{TITHI_COPY[language].loading}</p>
    </div>
  );
}

function ControlTithiDetails({
  language,
  tithi,
  typeInfo,
}: {
  language: TithiLanguage;
  tithi: PanchangTithi;
  typeInfo: TithiTypeInfo;
}) {
  const name = displayTithiName(tithi.name, language);
  const secondaryName = language === "hi" ? tithi.name : TITHI_CONTROL_DATA[tithi.name]?.hi;
  const visibleNumber = tithi.number > 15 ? tithi.number - 15 : tithi.number;
  return (
    <>
      <p className="mb-2 text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        {displayPakshaName(tithi.paksha, language)}
      </p>
      <h2 className="mb-1 font-serif text-3xl font-bold text-ish-dark sm:text-4xl">{name}</h2>
      {secondaryName && secondaryName !== name && (
        <p className="mb-4 text-xl text-ish-text-muted">{secondaryName}</p>
      )}
      <div className="mb-5 flex items-center justify-center gap-3" aria-hidden="true">
        {Array.from({ length: 15 }, (_, index) => (
          <span
            key={index}
            className={`h-2 w-2 rounded-full ${index + 1 === visibleNumber ? "scale-150 bg-ish-accent" : "bg-ish-border"}`}
          />
        ))}
      </div>
      <div
        className={`mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-body-small font-semibold ${typeInfo.badgeClass}`}
      >
        {tithi.name === "Purnima" ? "🌕" : tithi.name === "Amavasya" ? "🌑" : "◉"} {typeInfo.label}
      </div>
      <p className="text-body-small text-ish-text-muted">{typeInfo.desc}</p>
    </>
  );
}

export function ControlSignificanceCard({
  language,
  isLoading,
  tithi,
}: {
  language: TithiLanguage;
  isLoading: boolean;
  tithi?: PanchangTithi;
}) {
  const info = tithi ? TITHI_CONTROL_DATA[tithi.name] : null;
  if (isLoading || !tithi || !info) return <ControlCardReserve variant="significance" />;
  const copy = TITHI_COPY[language];
  const name = displayTithiName(tithi.name, language);
  return (
    <div className="card-sacred mb-5 min-h-[156px] p-5">
      <h3 className="mb-3 text-body font-bold text-ish-dark">
        {language === "hi"
          ? `${name} का ${copy.significanceTitle}`
          : `${copy.significanceTitle} of ${name}`}
      </h3>
      <p className="text-body-small leading-relaxed text-ish-text-muted">
        {language === "hi" ? info.significanceHi : info.significance}
      </p>
      <p className="mt-3 border-t border-ish-border pt-3 text-body-small text-ish-text-faint">
        <span className="font-semibold text-ish-dark">{copy.auspiciousForNewWork}: </span>
        {info.auspicious ? copy.auspiciousYes : copy.auspiciousNo}
      </p>
    </div>
  );
}

export function ControlPanchangSnapshot({
  language,
  isLoading,
  result,
  cityName,
}: {
  language: TithiLanguage;
  isLoading: boolean;
  result?: PanchangResponse;
  cityName: string;
}) {
  if (isLoading || !result) return <ControlCardReserve variant="snapshot" />;
  const copy = TITHI_COPY[language];
  const items = [
    {
      label: copy.tithi,
      value: `${displayTithiName(result.tithi.name, language)} (${displayPakshaName(
        result.tithi.paksha,
        language
      )})`,
    },
    { label: copy.nakshatra, value: result.nakshatra.name },
    { label: copy.yoga, value: result.yoga.name },
    { label: copy.karana, value: result.karana.name },
    { label: copy.vara, value: displayVaraName(result.vara, language) },
    { label: copy.sunrise, value: result.sunrise },
  ];
  return (
    <div className="card-sacred mb-5 min-h-[244px] p-5">
      <h3 className="mb-4 text-body font-bold text-ish-dark">
        {copy.snapshotTitle} · {cityName}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.label} className="rounded-lg bg-surface-warm p-3">
            <p className="mb-1 text-eyebrow uppercase tracking-eyebrow text-ish-text-faint">
              {item.label}
            </p>
            <p className="text-body-small font-semibold text-ish-dark">{item.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 text-center">
        <Link
          href={language === "hi" ? "/hi/panchang/" : "/panchang/"}
          className="text-body-small font-semibold text-intent-brand"
        >
          {copy.fullPanchang} →
        </Link>
      </div>
    </div>
  );
}

export function ControlTithiTypesReference({ language }: { language: TithiLanguage }) {
  return <TithiTypesReference language={language} />;
}

const RESERVE_HEIGHT_CLASS = {
  significance: "min-h-[156px]",
  snapshot: "min-h-[244px]",
} as const;

function ControlCardReserve({ variant }: { variant: keyof typeof RESERVE_HEIGHT_CLASS }) {
  return (
    <div aria-hidden="true" className={`card-sacred mb-5 p-5 ${RESERVE_HEIGHT_CLASS[variant]}`}>
      <div className="h-4 w-36 rounded bg-surface-warm" />
      <div className="mt-4 space-y-2">
        <div className="h-3 w-full rounded bg-surface-warm" />
        <div className="h-3 w-5/6 rounded bg-surface-warm" />
        <div className="h-3 w-2/3 rounded bg-surface-warm" />
      </div>
    </div>
  );
}
